import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Tooltip, message } from 'antd';
import {
    LoadingOutlined,
    CheckOutlined,
} from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTaskDrawer } from '../TaskDrawerContext';
import { uploadAttachment, getDownloadUrl, updateTask, getAttachmentPreviewUrl } from '../../api/projectManagementApi';
import { isImageFile, setOptimisticImageUrl, getOptimisticImageUrl, deleteOptimisticImageUrl } from '../../utils/attachmentUtils';

// Allow blob: and relative /api/... preview URLs in Quill so optimistic images & file links never get sanitized into "//:0"
const QuillInstance = ReactQuill.Quill;
if (QuillInstance) {
    const ImageFormat = QuillInstance.import('formats/image');
    if (ImageFormat) {
        ImageFormat.sanitize = (url) => {
            if (typeof url === 'string' && /^(https?:|data:|blob:|\/)/i.test(url.trim())) {
                return url.trim();
            }
            return '//:0';
        };
    }
    const LinkFormat = QuillInstance.import('formats/link');
    if (LinkFormat && Array.isArray(LinkFormat.PROTOCOL_WHITELIST) && !LinkFormat.PROTOCOL_WHITELIST.includes('blob')) {
        LinkFormat.PROTOCOL_WHITELIST.push('blob');
    }
}

function DescriptionFileMentionChip({ fileName, taskId, taskAttachments = [], onRemoveFile }) {
    const [loading, setLoading] = useState(false);
    const cleanName = (fileName || '').trim();

    // Flexible attachment matcher
    const matchedAtt = (taskAttachments || []).find((a) => {
        const attName = (a.originalName || a.fileName || '').trim().toLowerCase();
        const searchName = cleanName.toLowerCase();
        return attName === searchName || (attName && searchName.includes(attName)) || (searchName && attName.includes(searchName));
    });

    const handleClick = (e) => {
        e.stopPropagation();
        const url = matchedAtt?.publicUrl || matchedAtt?.downloadUrl;
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
        }
        const isValidId = matchedAtt?._id && /^[a-f0-9]{24}$/i.test(String(matchedAtt._id));
        if (isValidId && taskId && taskId !== 'new') {
            setLoading(true);
            getDownloadUrl(taskId, matchedAtt._id, 'inline')
                .then(({ data }) => {
                    const targetUrl = data?.result?.downloadUrl || data?.result?.publicUrl;
                    if (targetUrl) window.open(targetUrl, '_blank', 'noopener,noreferrer');
                    else message.error('Could not open file in new tab.');
                })
                .catch(() => message.error('Could not open file in new tab.'))
                .finally(() => setLoading(false));
        } else {
            message.info(`Referenced: ${cleanName}`);
        }
    };

    const handleDownload = (e) => {
        e.stopPropagation();
        const url = matchedAtt?.publicUrl || matchedAtt?.downloadUrl;
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.download = cleanName;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
            return;
        }
        const isValidId = matchedAtt?._id && /^[a-f0-9]{24}$/i.test(String(matchedAtt._id));
        if (isValidId && taskId && taskId !== 'new') {
            setLoading(true);
            getDownloadUrl(taskId, matchedAtt._id, 'attachment')
                .then(({ data }) => {
                    const targetUrl = data?.result?.downloadUrl || data?.result?.publicUrl;
                    if (targetUrl) {
                        const link = document.createElement('a');
                        link.href = targetUrl;
                        link.download = cleanName;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                    } else {
                        message.error('Could not download file.');
                    }
                })
                .catch(() => message.error('Could not download file.'))
                .finally(() => setLoading(false));
        } else {
            message.info(`File reference: ${cleanName}`);
        }
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        if (onRemoveFile) {
            onRemoveFile(fileName);
        }
    };

    return (
        <span
            onClick={handleClick}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 mx-1 my-0.5 rounded-lg border border-blue-200 dark:border-blue-900/60 bg-blue-50/90 dark:bg-[#1a2538] hover:bg-blue-100/90 dark:hover:bg-[#22324d] text-blue-800 dark:text-blue-300 text-xs font-medium cursor-pointer shadow-xs transition-all align-middle select-none group/filechip"
            title={`Click to view file: ${cleanName}`}
        >
            <span className="material-symbols-outlined text-sm text-blue-600 dark:text-blue-400">attach_file</span>
            <span className="font-semibold truncate max-w-[200px]">{cleanName}</span>
            <span className="text-[10px] text-blue-600 dark:text-blue-400 font-bold px-1 bg-blue-100 dark:bg-blue-900/40 rounded">
                @File
            </span>
            {loading ? (
                <span className="material-symbols-outlined text-xs animate-spin ml-1">sync</span>
            ) : (
                <span className="inline-flex items-center gap-0.5 ml-1" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Download file">
                        <button
                            type="button"
                            onClick={handleDownload}
                            className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-300 p-0.5 transition-colors flex items-center border-none bg-transparent cursor-pointer"
                        >
                            <span className="material-symbols-outlined text-[13px]">download</span>
                        </button>
                    </Tooltip>
                    {onRemoveFile && (
                        <Tooltip title="Remove file reference">
                            <button
                                type="button"
                                onClick={handleRemove}
                                className="text-slate-400 hover:text-red-500 p-0.5 transition-colors flex items-center border-none bg-transparent cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-[13px]">close</span>
                            </button>
                        </Tooltip>
                    )}
                </span>
            )}
        </span>
    );
}

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'code'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        ['blockquote', 'code-block'],
        ['link'],
        ['clean'],
    ],
};

const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'code',
    'list', 'bullet',
    'color', 'background',
    'blockquote', 'code-block',
    'link', 'image',
];

const isQuillEmpty = (content) => {
    if (!content) return true;
    const stripped = content.replace(/<[^>]*>/g, '').trim();
    const hasMedia = /<img|<iframe|<video/i.test(content);
    return stripped.length === 0 && !hasMedia;
};

const extractEmbeddedAttachmentIds = (html) => {
    if (!html || typeof html !== 'string') return [];
    const ids = new Set();
    const regex = /\/attachments\/([a-f0-9]{24})\/preview/gi;
    let match;
    while ((match = regex.exec(html)) !== null) {
        ids.add(match[1]);
    }
    return Array.from(ids);
};

export default function TaskDescription() {
    const {
        taskId,
        task,
        setTask,
        editDescription,
        setEditDescription,
        improvingDescription,
        preImproveDescription,
        handleImproveDescription,
        handleUndoImproveDescription,
        mode,
        itemType,
        isAutoSaving,
        scheduleAutoSave,
        handleDeleteAttachment,
        handleBulkDeleteAttachments,
        pendingAttachments,
        setPendingAttachments,
    } = useTaskDrawer();

    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [hoveredImage, setHoveredImage] = useState(null);
    const hoverLeaveTimerRef = useRef(null);
    const lastSelectionRangeRef = useRef(null);
    const knownEmbeddedAttachmentIdsRef = useRef([]);
    const quillRef = useRef(null);
    const editorWrapperRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    const taskAttachments = task?.attachments || [];

    // Auto-heal broken/legacy image & file link URLs in description on load
    useEffect(() => {
        if (!editDescription || !taskId || taskId === 'new' || uploadingFile) return;

        const imgRegex = /<img[^>]+src="([^">]+)"/gi;
        let match;
        let needsHeal = false;
        let healedHtml = editDescription;
        const imageAtts = taskAttachments.filter((a) => isImageFile(a));

        while ((match = imgRegex.exec(editDescription)) !== null) {
            const rawSrc = match[1];
            // If it's a live blob from this current session, leave it alone
            if (rawSrc.startsWith('blob:') && getOptimisticImageUrl(rawSrc)) continue;

            const isDeadBlob = rawSrc.startsWith('blob:') && !getOptimisticImageUrl(rawSrc);
            const isR2Direct = rawSrc.includes('r2.cloudflarestorage.com');
            const isMissingPreview = !rawSrc.includes('/preview') && (isDeadBlob || isR2Direct);

            if (isMissingPreview && imageAtts.length > 0) {
                // Only match if the source actually contains the attachment's specific ID or original filename
                const matched = imageAtts.find((a) => a._id && rawSrc.includes(String(a._id)))
                    || imageAtts.find((a) => a.originalName && a.originalName.length > 3 && rawSrc.includes(encodeURIComponent(a.originalName)));

                if (matched && matched._id) {
                    const previewUrl = getAttachmentPreviewUrl(taskId, matched._id);
                    healedHtml = healedHtml.replaceAll(rawSrc, previewUrl);
                    needsHeal = true;
                } else if (isDeadBlob) {
                    // Dead blob from an expired browser session with no matching attachment — remove it
                    healedHtml = healedHtml.replaceAll(match[0], '');
                    needsHeal = true;
                }
            } else if (isDeadBlob) {
                healedHtml = healedHtml.replaceAll(match[0], '');
                needsHeal = true;
            }
        }

        // Also heal any <a href="blob:..."> file links using their display text
        const anchorRegex = /<a[^>]+href="(blob:[^">]+)"[^>]*>(.*?)<\/a>/gi;
        let aMatch;
        while ((aMatch = anchorRegex.exec(editDescription)) !== null) {
            const rawHref = aMatch[1];
            if (getOptimisticImageUrl(rawHref)) continue;

            const innerText = (aMatch[2] || '').replace(/<[^>]*>/g, '').replace(/📎/g, '').trim();
            if (innerText && taskAttachments.length > 0) {
                const matchedAtt = taskAttachments.find((a) => {
                    const aName = (a.originalName || a.fileName || '').trim().toLowerCase();
                    return aName && aName.length > 2 && (innerText.toLowerCase() === aName || innerText.toLowerCase().includes(aName));
                });
                if (matchedAtt?._id) {
                    const previewUrl = getAttachmentPreviewUrl(taskId, matchedAtt._id);
                    healedHtml = healedHtml.replaceAll(rawHref, previewUrl);
                    needsHeal = true;
                } else {
                    healedHtml = healedHtml.replaceAll(aMatch[0], '');
                    needsHeal = true;
                }
            } else {
                healedHtml = healedHtml.replaceAll(aMatch[0], '');
                needsHeal = true;
            }
        }

        if (needsHeal && healedHtml !== editDescription) {
            setEditDescription(healedHtml);
            if (quillRef.current) {
                const editor = quillRef.current.getEditor();
                if (editor.root.innerHTML !== healedHtml) {
                    editor.root.innerHTML = healedHtml;
                }
            }
            if (scheduleAutoSave && mode === 'view') {
                scheduleAutoSave({ description: healedHtml });
            }
        }
    }, [taskId, taskAttachments.length]);

    // Keep track of embedded description attachment IDs
    useEffect(() => {
        if (editDescription) {
            const found = extractEmbeddedAttachmentIds(editDescription);
            if (found.length > 0) {
                knownEmbeddedAttachmentIdsRef.current = Array.from(new Set([...knownEmbeddedAttachmentIdsRef.current, ...found]));
            }
        }
    }, [editDescription]);

    // Extract file names referenced via @File[filename]
    const mentionedFileNames = useMemo(() => {
        if (!editDescription || typeof editDescription !== 'string') return [];
        const matches = [];
        const regex = /@File\[([^\]]+)\]/g;
        let m;
        while ((m = regex.exec(editDescription)) !== null) {
            if (!matches.includes(m[1])) matches.push(m[1]);
        }
        return matches;
    }, [editDescription]);

    // Calculate exact character index for pasted content
    const getPasteIndex = () => {
        if (!quillRef.current) return 0;
        const editor = quillRef.current.getEditor();

        // 1. Check native DOM selection inside Quill editor root
        try {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0 && editor.root && editor.root.contains(sel.anchorNode)) {
                const range = sel.getRangeAt(0);
                const blot = ReactQuill.Quill?.find?.(range.startContainer) || editor.scroll?.find?.(range.startContainer);
                if (blot) {
                    return editor.getIndex(blot) + range.startOffset;
                }
            }
        } catch { /* fallback */ }

        // 2. Check Quill's active selection (forces sync from DOM)
        try {
            const qSel = editor.getSelection(true);
            if (qSel && typeof qSel.index === 'number') {
                return qSel.index;
            }
        } catch { /* fallback */ }

        // 3. Fallback to last recorded selection
        if (lastSelectionRangeRef.current && typeof lastSelectionRangeRef.current.index === 'number') {
            return lastSelectionRangeRef.current.index;
        }

        // 4. Default to editor length
        return editor.getLength();
    };

    // Calculate exact character index under drop coordinates
    const getDropIndex = (e) => {
        if (!quillRef.current) return null;
        const editor = quillRef.current.getEditor();
        let targetIndex = null;

        if (document.caretRangeFromPoint) {
            const range = document.caretRangeFromPoint(e.clientX, e.clientY);
            if (range) {
                const blot = ReactQuill.Quill?.find?.(range.startContainer) || editor.scroll?.find?.(range.startContainer);
                if (blot) {
                    targetIndex = editor.getIndex(blot) + range.startOffset;
                }
            }
        } else if (document.caretPositionFromPoint) {
            const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
            if (pos) {
                const blot = ReactQuill.Quill?.find?.(pos.offsetNode) || editor.scroll?.find?.(pos.offsetNode);
                if (blot) {
                    targetIndex = editor.getIndex(blot) + pos.offset;
                }
            }
        }

        if (targetIndex === null || isNaN(targetIndex)) {
            const sel = editor.getSelection();
            targetIndex = sel ? sel.index : editor.getLength();
        }
        return Math.min(Math.max(0, targetIndex), editor.getLength());
    };

    // Multi-file upload handler for description:
    // - Dropped/pasted IMAGES show INSTANTLY (0ms) and upload to R2 with persistent preview streaming URL
    // - Non-image files upload to R2 and insert inline file link cards right where dropped/pasted
    // - Changes automatically save debounced to backend
    const handleFilesUpload = async (fileList, targetPosition = null) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        setUploadingFile(true);
        const editor = quillRef.current ? quillRef.current.getEditor() : null;
        const optimisticEntries = [];

        // 1. Optimistic insertion: immediately show images and file references right where dropped / pasted / cursor
        if (editor) {
            let idx = (typeof targetPosition === 'number' && !isNaN(targetPosition))
                ? targetPosition
                : getPasteIndex();

            idx = Math.min(Math.max(0, idx), editor.getLength());

            files.forEach((file) => {
                if (isImageFile(file)) {
                    const tempBlobUrl = URL.createObjectURL(file);
                    optimisticEntries.push({ file, tempBlobUrl, isImg: true, insertIdx: idx });
                    setOptimisticImageUrl(file.name, tempBlobUrl);
                    setOptimisticImageUrl(tempBlobUrl, tempBlobUrl);

                    editor.insertEmbed(idx, 'image', tempBlobUrl, 'user');
                    idx += 1;
                    editor.insertText(idx, '\n', 'user');
                    idx += 1;
                } else {
                    const tempBlobUrl = URL.createObjectURL(file);
                    const fileText = `📎 ${file.name}`;
                    optimisticEntries.push({ file, tempBlobUrl, isImg: false, fileText, insertIdx: idx });
                    setOptimisticImageUrl(file.name, tempBlobUrl);
                    setOptimisticImageUrl(tempBlobUrl, tempBlobUrl);

                    editor.insertText(idx, fileText, 'link', tempBlobUrl, 'user');
                    idx += fileText.length;
                    // Explicitly clear the 'link' format on the trailing space so adjacent files never merge into one <a> tag
                    editor.insertText(idx, ' ', { link: false }, 'user');
                    idx += 1;
                }
            });

            editor.setSelection(idx, 0);
            if (lastSelectionRangeRef.current) {
                lastSelectionRangeRef.current = { index: idx, length: 0 };
            }
            if (!taskId || taskId === 'new') {
                const immediateHtml = editor.root.innerHTML;
                setEditDescription(immediateHtml);
            }
        }

        try {
            if (taskId && taskId !== 'new') {
                const results = await Promise.all(
                    files.map(async (file, i) => {
                        try {
                            const fd = new FormData();
                            fd.append('file', file);
                            const { data } = await uploadAttachment(taskId, fd);
                            const newAtt = data?.result;
                            const previewUrl = (newAtt?._id ? getAttachmentPreviewUrl(taskId, newAtt._id) : null) || newAtt?.previewUrl;
                            let persistentUrl = previewUrl || newAtt?.publicUrl || newAtt?.downloadUrl;
                            if (!persistentUrl && newAtt?._id) {
                                try {
                                    const dl = await getDownloadUrl(taskId, newAtt._id, 'inline');
                                    persistentUrl = dl?.data?.result?.downloadUrl || dl?.data?.result?.publicUrl;
                                } catch (e) {
                                    console.warn('Could not fetch presigned inline URL for attachment:', e);
                                }
                            }
                            return { file, newAtt, persistentUrl, previewUrl, tempBlobUrl: optimisticEntries[i]?.tempBlobUrl };
                        } catch (err) {
                            console.error(`Failed to upload ${file.name}:`, err);
                            return { file, newAtt: null, persistentUrl: null, previewUrl: null, tempBlobUrl: optimisticEntries[i]?.tempBlobUrl };
                        }
                    })
                );

                const newAtts = results.map((r) => r.newAtt).filter(Boolean);
                if (newAtts.length > 0 && setTask) {
                    setTask((prev) => (prev ? {
                        ...prev,
                        attachments: [
                            ...(prev.attachments || []).filter((a) => !newAtts.some((n) => n._id === a._id)),
                            ...newAtts,
                        ],
                    } : prev));
                }

                if (editor) {
                    let updatedHtml = editor.root.innerHTML;
                    results.forEach((r) => {
                        const targetUrl = r.previewUrl || r.persistentUrl;
                        if (targetUrl) {
                            if (r.newAtt?._id) setOptimisticImageUrl(r.newAtt._id, targetUrl);
                            if (r.newAtt?.originalName) setOptimisticImageUrl(r.newAtt.originalName, targetUrl);
                            if (r.file?.name) setOptimisticImageUrl(r.file.name, targetUrl);

                            if (r.tempBlobUrl) {
                                updatedHtml = updatedHtml.replaceAll(r.tempBlobUrl, targetUrl);
                                setOptimisticImageUrl(r.tempBlobUrl, targetUrl);
                            }
                        }
                        if (r.newAtt && r.newAtt.originalName && r.file.name !== r.newAtt.originalName) {
                            updatedHtml = updatedHtml.replaceAll(r.file.name, r.newAtt.originalName);
                        }
                    });

                    editor.root.innerHTML = updatedHtml;
                    setEditDescription(updatedHtml);
                    const newAttIds = results.map((r) => r.newAtt?._id).filter(Boolean).map(String);
                    if (newAttIds.length > 0) {
                        knownEmbeddedAttachmentIdsRef.current = Array.from(new Set([...knownEmbeddedAttachmentIdsRef.current, ...newAttIds]));
                    }
                    scheduleAutoSave?.({ description: updatedHtml });
                }

                message.success(`Attached ${files.length} file(s) to description.`);
            } else {
                // Create mode: queue files in pendingAttachments so they upload to R2 when the task is created
                // and their temporary blob: URLs in the description are automatically replaced with permanent preview URLs
                if (setPendingAttachments && optimisticEntries.length > 0) {
                    const newPendingEntries = optimisticEntries.map((entry, idx) => ({
                        id: `desc_pending_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 7)}`,
                        file: entry.file,
                        name: entry.file.name,
                        size: entry.file.size,
                        previewUrl: entry.tempBlobUrl,
                        tempBlobUrl: entry.tempBlobUrl,
                        status: 'pending',
                        progress: 0,
                        error: '',
                    }));
                    setPendingAttachments((prev) => [...(prev || []), ...newPendingEntries]);
                }
                message.success(`Added ${files.length} file(s) to description (will upload when task is created).`);
            }
        } catch (e) {
            console.error('Error uploading description attachments:', e);
            message.error('Failed to attach files to description.');
        } finally {
            setUploadingFile(false);
        }
    };

    // Paste handler for Quill editor (handles screenshots, copied images, and files)
    const handleQuillPaste = (e) => {
        const clipboardData = e.clipboardData || window.clipboardData;
        if (!clipboardData) return;

        const items = clipboardData.items;
        const files = clipboardData.files;
        const pastedFiles = [];
        const seenSizes = new Set();

        // 1. DataTransferItemList - screenshots / copied clipboard image data
        if (items && items.length > 0) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.type && item.type.startsWith('image/')) {
                    const blob = item.getAsFile();
                    if (blob) {
                        const ext = blob.type ? blob.type.split('/')[1] || 'png' : 'png';
                        const cleanExt = ext === 'jpeg' ? 'jpg' : ext.replace('+xml', '');
                        const timestamp = Date.now();
                        const fileName = (blob.name && blob.name !== 'image.png')
                            ? blob.name
                            : `screenshot-${timestamp}-${i + 1}.${cleanExt}`;
                        const fileObj = new File([blob], fileName, { type: blob.type || 'image/png' });
                        pastedFiles.push(fileObj);
                        seenSizes.add(`${fileObj.size}_${fileObj.type}`);
                    }
                }
            }
        }

        // 2. DataTransfer files fallback (files copied from desktop/explorer, deduplicating any already extracted image)
        if (files && files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const key = `${file.size}_${file.type}`;
                // Avoid duplicating screenshots/copied images that the browser exposed in both items and files
                if (!seenSizes.has(key) && !pastedFiles.some((f) => f.name === file.name)) {
                    pastedFiles.push(file);
                    seenSizes.add(key);
                }
            }
        }

        if (pastedFiles.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            const pasteIdx = getPasteIndex();
            handleFilesUpload(pastedFiles, pasteIdx);
            return;
        }

        // Plain text / URL paste handler — ensures cursor lands AFTER the pasted link instead of jumping to 0
        const pastedText = clipboardData.getData('text/plain');
        if (pastedText && quillRef.current) {
            const editor = quillRef.current.getEditor();
            if (editor) {
                e.preventDefault();
                e.stopPropagation();
                const range = editor.getSelection(true) || lastSelectionRangeRef.current || { index: editor.getLength(), length: 0 };
                const insertIndex = typeof range.index === 'number' ? range.index : editor.getLength();
                const isUrl = /^https?:\/\/[^\s]+$/i.test(pastedText.trim());

                if (isUrl) {
                    const url = pastedText.trim();
                    editor.insertText(insertIndex, url, 'link', url, 'user');
                    editor.setSelection(insertIndex + url.length, 0, 'user');
                } else {
                    editor.insertText(insertIndex, pastedText, 'user');
                    editor.setSelection(insertIndex + pastedText.length, 0, 'user');
                }

                const newHtml = editor.root.innerHTML;
                setEditDescription(newHtml);
                if (mode === 'view' && taskId && taskId !== 'new') {
                    scheduleAutoSave?.({ description: newHtml });
                }
            }
        }
    };

    // Drag and drop handlers with exact cursor position calculation
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);
        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            const dropIndex = getDropIndex(e);
            handleFilesUpload(files, dropIndex);
        }
    };

    // Match task attachment ID(s) referenced by an <img> or <a> element in the description editor
    const matchAttachmentsForElement = (el) => {
        if (!el) return [];
        const matchedIds = new Set();
        const rawUrl = (el.getAttribute('src') || el.getAttribute('href') || el.src || el.href || '').trim();

        if (rawUrl) {
            const previewMatches = rawUrl.matchAll(/\/attachments\/([a-f0-9]{24})\/preview/gi);
            for (const m of previewMatches) {
                if (m[1]) matchedIds.add(String(m[1]));
            }
            (taskAttachments || []).forEach((a) => {
                const aId = String(a._id || '');
                const aName = a.originalName || a.fileName || '';
                if ((aId && rawUrl.includes(aId)) || (aName && rawUrl.includes(encodeURIComponent(aName)))) {
                    if (aId) matchedIds.add(aId);
                }
            });
        }

        if (el.tagName === 'A') {
            const rawText = (el.textContent || '').trim();
            // Support both single file pills ("📎 File.xlsx") and legacy merged pills ("📎 File1.xlsx 📎 File2.xlsx")
            const segments = rawText
                .split('📎')
                .map((s) => s.trim())
                .filter(Boolean);
            if (rawText && !segments.includes(rawText.replace(/📎/g, '').trim())) {
                segments.push(rawText.replace(/📎/g, '').trim());
            }
            segments.forEach((seg) => {
                const lowerSeg = seg.toLowerCase();
                (taskAttachments || []).forEach((a) => {
                    const aId = String(a._id || '');
                    const aName = (a.originalName || a.fileName || '').trim().toLowerCase();
                    if (aId && aName && (aName === lowerSeg || lowerSeg.includes(aName))) {
                        matchedIds.add(aId);
                    }
                });
            });
        }

        return Array.from(matchedIds);
    };

    // Open an embedded <img> or <a> file link from the floating toolbar
    const handleOpenEmbeddedElement = async (targetInfo) => {
        const info = targetInfo || hoveredImage || selectedImage;
        if (!info?.element) return;
        const el = info.element;
        const rawUrl = (el.getAttribute('src') || el.getAttribute('href') || info.src || '').trim();

        const cachedUrl = rawUrl ? getOptimisticImageUrl(rawUrl) : null;
        if (cachedUrl && !cachedUrl.startsWith('blob:')) {
            window.open(cachedUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (rawUrl && !rawUrl.startsWith('blob:')) {
            window.open(rawUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        // Resolve via matched task attachment if URL is a dead/session blob
        const matchedIds = matchAttachmentsForElement(el);
        if (matchedIds.length > 0 && taskId && taskId !== 'new') {
            const firstId = matchedIds[0];
            const isValidId = firstId && /^[a-f0-9]{24}$/i.test(String(firstId));
            if (isValidId) {
                const matchedAtt = (taskAttachments || []).find((a) => String(a._id) === firstId);
                if (matchedAtt?.publicUrl || matchedAtt?.downloadUrl) {
                    window.open(matchedAtt.publicUrl || matchedAtt.downloadUrl, '_blank', 'noopener,noreferrer');
                    return;
                }
                try {
                    const { data } = await getDownloadUrl(taskId, firstId, 'inline');
                    const targetUrl = data?.result?.downloadUrl || data?.result?.publicUrl || getAttachmentPreviewUrl(taskId, firstId);
                    if (targetUrl) {
                        window.open(targetUrl, '_blank', 'noopener,noreferrer');
                        return;
                    }
                } catch {
                    window.open(getAttachmentPreviewUrl(taskId, firstId), '_blank', 'noopener,noreferrer');
                    return;
                }
            }
        }

        if (rawUrl) {
            window.open(rawUrl, '_blank', 'noopener,noreferrer');
        } else {
            message.warning('Could not open file.');
        }
    };

    // Delete embedded image (<img>) or file link (<a>) from description, MongoDB, and caches (never delete from R2)
    const handleDeleteImage = async (targetElement) => {
        const el = targetElement || hoveredImage?.element || selectedImage?.element;
        if (!el || !quillRef.current) return;

        const isAnchor = el.tagName === 'A';
        const rawUrl = (el.getAttribute('src') || el.getAttribute('href') || el.src || el.href || '').trim();
        const matchedAttIds = matchAttachmentsForElement(el);

        if (rawUrl) {
            deleteOptimisticImageUrl(rawUrl);
        }

        // If in create mode, also remove the corresponding pending attachment from the queue
        if ((!taskId || taskId === 'new') && setPendingAttachments && rawUrl) {
            setPendingAttachments((prev) => (prev || []).filter((item) => item.tempBlobUrl !== rawUrl && item.previewUrl !== rawUrl));
        }

        const editor = quillRef.current.getEditor();
        if (isAnchor) {
            el.remove();
        } else {
            const blot = ReactQuill.Quill?.find?.(el) || editor.scroll?.find?.(el);
            if (blot) {
                const idx = editor.getIndex(blot);
                editor.deleteText(idx, 1, 'user');
            } else {
                el.remove();
            }
        }

        setHoveredImage(null);
        setSelectedImage(null);

        const newHtml = editor.root.innerHTML;
        setEditDescription(newHtml);

        if (matchedAttIds.length > 0) {
            knownEmbeddedAttachmentIdsRef.current = knownEmbeddedAttachmentIdsRef.current.filter(
                (id) => !matchedAttIds.includes(String(id))
            );
        }

        // Delete from MongoDB task attachments & storage metadata (never from Cloudflare R2)
        if (matchedAttIds.length === 1 && handleDeleteAttachment && taskId && taskId !== 'new') {
            await handleDeleteAttachment(matchedAttIds[0], newHtml);
        } else if (matchedAttIds.length > 1 && handleBulkDeleteAttachments && taskId && taskId !== 'new') {
            await handleBulkDeleteAttachments(matchedAttIds, newHtml);
        } else {
            if (mode === 'view' && taskId && taskId !== 'new') {
                try {
                    await updateTask(taskId, { description: newHtml });
                    if (setTask) {
                        setTask((prev) => (prev ? { ...prev, description: newHtml } : prev));
                    }
                } catch {
                    scheduleAutoSave?.({ description: newHtml });
                }
            }
            message.success(isAnchor ? 'File removed from description.' : 'Image removed from description.');
        }
    };

    const handleDeleteSelectedImage = () => handleDeleteImage(selectedImage?.element);

    const handleRemoveFileReference = async (targetFileName) => {
        const cleanTarget = (targetFileName || '').trim();
        const escaped = cleanTarget.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        let newDesc = (editDescription || '')
            .replace(new RegExp(`\\s*@File\\[${escaped}\\]\\s*`, 'g'), ' ')
            .replace(new RegExp(`<a[^>]*>[^<]*${escaped}[^<]*<\\/a>`, 'gi'), ' ')
            .trim();

        setEditDescription(newDesc);
        if (quillRef.current) {
            quillRef.current.getEditor().root.innerHTML = newDesc;
        }

        const matched = (taskAttachments || []).find((a) => {
            const attName = (a.originalName || a.fileName || '').trim().toLowerCase();
            return attName === cleanTarget.toLowerCase();
        });

        if (matched?._id && handleDeleteAttachment) {
            await handleDeleteAttachment(matched._id, newDesc);
        } else if (taskId && taskId !== 'new') {
            try {
                await updateTask(taskId, { description: newDesc });
                if (setTask) {
                    setTask((prev) => (prev ? { ...prev, description: newDesc } : prev));
                }
                message.success(`Removed "${cleanTarget}" from description.`);
            } catch {
                message.error('Failed to update description.');
            }
        }
    };

    // Clean up any description-embedded attachments that were removed from HTML (e.g. via Select All + Backspace)
    const cleanupOrphanDescriptionAttachments = (currentHtml) => {
        if (!handleBulkDeleteAttachments || !taskId || taskId === 'new') return;
        const remainingIds = new Set(extractEmbeddedAttachmentIds(currentHtml));
        const orphanIds = knownEmbeddedAttachmentIdsRef.current.filter((id) => !remainingIds.has(id));
        if (orphanIds.length > 0) {
            knownEmbeddedAttachmentIdsRef.current = Array.from(remainingIds);
            handleBulkDeleteAttachments(orphanIds, currentHtml);
        }
    };

    const handleEditorBlur = () => {
        if (mode === 'view' && taskId && taskId !== 'new') {
            if (scheduleAutoSave) {
                scheduleAutoSave({ description: editDescription });
            }
            cleanupOrphanDescriptionAttachments(editDescription);
        }
    };

    return (
        <section
            data-tour="task-description-section"
            className={isFullscreen
                ? "fixed inset-0 z-[1100] bg-white dark:bg-[#1a1a1a] p-6 flex flex-col overflow-hidden shadow-2xl space-y-3"
                : "space-y-2 px-1"
            }
        >
            <style>{`
                .task-quill-wrapper .ql-toolbar.ql-snow {
                    border: 1px solid #e2e8f0;
                    border-bottom: 1px solid #f1f5f9;
                    background: #f8fafc;
                    border-top-left-radius: 0.75rem;
                    border-top-right-radius: 0.75rem;
                    padding: 6px 10px;
                }
                /* Prevent nested scroll trapping: editor expands naturally with drawer scroll */
                .task-quill-wrapper:not(.is-fullscreen) .ql-container.ql-snow {
                    height: auto !important;
                }
                .task-quill-wrapper:not(.is-fullscreen) .ql-editor {
                    height: auto !important;
                    min-height: 160px;
                    overflow-y: visible !important;
                    font-size: 13px;
                    line-height: 1.6;
                    color: #1e293b;
                    padding: 10px 14px;
                }
                .task-quill-wrapper.is-fullscreen {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    min-height: 0;
                    height: 100%;
                }
                .task-quill-wrapper.is-fullscreen .quill {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    min-height: 0;
                    height: 100%;
                }
                .task-quill-wrapper.is-fullscreen .ql-toolbar.ql-snow {
                    flex-shrink: 0;
                }
                .task-quill-wrapper.is-fullscreen .ql-container.ql-snow {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    min-height: 0;
                    height: 100%;
                    overflow: hidden;
                }
                .task-quill-wrapper.is-fullscreen .ql-editor {
                    flex: 1;
                    height: 100%;
                    min-height: 0;
                    max-height: none;
                    overflow-y: auto !important;
                    font-size: 14px;
                    line-height: 1.7;
                    padding: 16px 20px;
                }
                .task-quill-wrapper.is-fullscreen .ql-editor::-webkit-scrollbar {
                    width: 8px;
                }
                .task-quill-wrapper.is-fullscreen .ql-editor::-webkit-scrollbar-track {
                    background: transparent;
                }
                .task-quill-wrapper.is-fullscreen .ql-editor::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 4px;
                }
                .dark .task-quill-wrapper.is-fullscreen .ql-editor::-webkit-scrollbar-thumb {
                    background: #475569;
                }
                .task-quill-wrapper.is-fullscreen .ql-editor::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
                .task-quill-wrapper .ql-editor.ql-blank::before {
                    color: #94a3b8;
                    font-style: italic;
                    font-size: 13px;
                    left: 14px;
                }
                .dark .task-quill-wrapper .ql-toolbar.ql-snow {
                    background: #182234;
                    border-color: #334155;
                    border-bottom-color: #1e293b;
                }
                .dark .task-quill-wrapper .ql-toolbar.ql-snow .ql-stroke {
                    stroke: #94a3b8 !important;
                }
                .dark .task-quill-wrapper .ql-toolbar.ql-snow .ql-fill {
                    fill: #94a3b8 !important;
                }
                .dark .task-quill-wrapper .ql-toolbar.ql-snow .ql-picker {
                    color: #94a3b8 !important;
                }
                .dark .task-quill-wrapper .ql-toolbar.ql-snow .ql-picker-options {
                    background-color: #1e293b !important;
                    border-color: #334155 !important;
                    color: #e2e8f0 !important;
                }
                .dark .task-quill-wrapper .ql-container.ql-snow {
                    background: #141414;
                    border-color: #334155;
                }
                .dark .task-quill-wrapper .ql-editor {
                    color: #f1f5f9;
                }
                .dark .task-quill-wrapper .ql-editor.ql-blank::before {
                    color: #64748b;
                }
                .task-quill-wrapper .ql-editor a {
                    color: #03488a !important;
                    text-decoration: none !important;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 2px 8px;
                    margin: 2px 2px;
                    border-radius: 6px;
                    background: #f0f7ff;
                    border: 1px solid #bae0ff;
                    font-weight: 600;
                    font-size: 12px;
                    cursor: pointer;
                    vertical-align: middle;
                    transition: all 0.15s ease;
                }
                .task-quill-wrapper .ql-editor a:hover {
                    background: #e6f4ff;
                    border-color: #03488a;
                    transform: translateY(-1px);
                    box-shadow: 0 1px 3px rgba(3, 72, 138, 0.12);
                }
                .dark .task-quill-wrapper .ql-editor a {
                    color: #91caff !important;
                    background: #11253e;
                    border-color: #1d39c4;
                }
                .dark .task-quill-wrapper .ql-editor a:hover {
                    background: #163c6e;
                    border-color: #69b1ff;
                }
                .task-quill-wrapper .ql-editor img {
                    max-width: 100%;
                    border-radius: 0.5rem;
                    margin: 0.5rem 0;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    cursor: pointer;
                    transition: all 0.15s ease;
                    content-visibility: auto;
                    contain: paint;
                }
                .task-quill-wrapper .ql-editor img:hover {
                    transform: scale(1.005);
                    box-shadow: 0 4px 12px rgba(3, 72, 138, 0.15);
                    outline: 2px solid #03488a;
                    outline-offset: 2px;
                }
            `}</style>

            <div className="flex justify-between items-center shrink-0">
                <div className="flex items-center gap-2">
                    <h3 className="text-[13px] font-bold text-[#111827] dark:text-white">Description</h3>
                    {mode === 'view' && (
                        isAutoSaving ? (
                            <span className="text-[11px] text-slate-400 dark:text-slate-500 flex items-center gap-1 font-normal animate-pulse">
                                <LoadingOutlined style={{ fontSize: 10 }} />
                                Saving…
                            </span>
                        ) : (
                            <span className="text-[11px] text-slate-400 dark:text-slate-500 flex items-center gap-1 font-normal opacity-70">
                                <CheckOutlined style={{ fontSize: 10, color: '#10b981' }} />
                                Saved
                            </span>
                        )
                    )}
                    {mode === 'create' && itemType !== 'milestone' && (
                        <>
                            <span className="text-red-500 font-bold">*</span>
                            {isQuillEmpty(editDescription) && (
                                <span className="text-xs text-red-500 dark:text-red-400 font-normal">Required</span>
                            )}
                        </>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {/* Attach File button */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                                handleFilesUpload(e.target.files);
                                e.target.value = '';
                            }
                        }}
                    />
                    <Tooltip title="Attach image or document to description">
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingFile}
                            className="flex items-center gap-1 text-slate-600 dark:text-slate-300 hover:text-primary font-medium text-xs transition-all disabled:opacity-40 cursor-pointer"
                        >
                            {uploadingFile ? (
                                <>
                                    <span className="material-symbols-outlined text-sm animate-spin">sync</span>
                                    Uploading…
                                </>
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-sm">attach_file</span>
                                    Attach File
                                </>
                            )}
                        </button>
                    </Tooltip>

                    {/* Undo button — only visible after AI has improved description */}
                    {preImproveDescription !== null && (
                        <Tooltip title="Undo AI improvement">
                            <button
                                type="button"
                                onClick={handleUndoImproveDescription}
                                className="flex items-center gap-1 text-ed-on-surface-variant text-xs hover:text-error transition-all cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-sm">undo</span>
                                Undo
                            </button>
                        </Tooltip>
                    )}

                    {/* Enhance with AI button */}
                    <button
                        type="button"
                        onClick={handleImproveDescription}
                        disabled={improvingDescription || !editDescription?.trim()}
                        className="flex items-center gap-1.5 text-primary font-medium text-xs hover:underline transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                        {improvingDescription ? (
                            <>
                                <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                                Enhancing…
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined text-sm">auto_awesome</span>
                                Enhance with AI
                            </>
                        )}
                    </button>

                    {/* Expand / Fullscreen Toggle button */}
                    <Tooltip title={isFullscreen ? "Exit Fullscreen (Esc)" : "Expand Description Editor"}>
                        <button
                            type="button"
                            onClick={() => setIsFullscreen(prev => !prev)}
                            className="flex items-center gap-1.5 text-slate-700 dark:text-slate-200 hover:text-primary dark:hover:text-[#a7c8ff] font-medium text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 transition-all cursor-pointer shadow-2xs"
                        >
                            <span className="material-symbols-outlined text-[15px] text-[#03488a] dark:text-[#a7c8ff]">
                                {isFullscreen ? 'close_fullscreen' : 'open_in_full'}
                            </span>
                            <span>{isFullscreen ? 'Exit' : 'Expand'}</span>
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Asana-style Live Editor with Drag & Drop */}
            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative rounded-xl transition-all ${isDraggingOver ? 'ring-2 ring-[#03488a] bg-blue-50/50 dark:bg-blue-950/30' : ''} ${isFullscreen ? 'flex-1 flex flex-col min-h-0 overflow-hidden' : ''}`}
            >
                {isDraggingOver && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-blue-50/90 dark:bg-slate-900/90 rounded-xl border-2 border-dashed border-[#03488a] pointer-events-none">
                        <div className="flex items-center gap-2 text-sm font-bold text-[#03488a] dark:text-blue-400">
                            <span className="material-symbols-outlined text-xl">cloud_upload</span>
                            Drop file here to attach to Description
                        </div>
                    </div>
                )}

                <div
                    ref={editorWrapperRef}
                    onPaste={handleQuillPaste}
                    onMouseMove={(e) => {
                        const img = e.target.tagName === 'IMG' ? e.target : e.target.closest('img');
                        const anchor = !img ? (e.target.tagName === 'A' ? e.target : e.target.closest('.ql-editor a')) : null;
                        const targetEl = img || anchor;

                        if (targetEl && editorWrapperRef.current?.contains(targetEl)) {
                            if (hoverLeaveTimerRef.current) {
                                clearTimeout(hoverLeaveTimerRef.current);
                                hoverLeaveTimerRef.current = null;
                            }
                            const isLink = targetEl.tagName === 'A';
                            const rect = targetEl.getBoundingClientRect();
                            const wrapperRect = editorWrapperRef.current.getBoundingClientRect();
                            const relTop = rect.top - wrapperRect.top;
                            const relLeft = rect.left - wrapperRect.left;
                            const top = isLink
                                ? (relTop > 38 ? relTop - 34 : (rect.bottom - wrapperRect.top + 4))
                                : Math.max(6, relTop + 6);
                            const left = isLink
                                ? Math.max(8, Math.min(relLeft, wrapperRect.width - 155))
                                : Math.max(8, relLeft + rect.width - 155);

                            setHoveredImage((prev) => {
                                if (prev?.element === targetEl && Math.abs(prev.top - top) < 2) return prev;
                                return {
                                    element: targetEl,
                                    src: targetEl.getAttribute(isLink ? 'href' : 'src'),
                                    isLink,
                                    top,
                                    left,
                                };
                            });
                        } else if (!e.target.closest('.image-floating-toolbar')) {
                            if (!hoverLeaveTimerRef.current && hoveredImage) {
                                hoverLeaveTimerRef.current = setTimeout(() => {
                                    setHoveredImage(null);
                                    hoverLeaveTimerRef.current = null;
                                }, 180);
                            }
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!e.relatedTarget?.closest?.('.image-floating-toolbar')) {
                            setHoveredImage(null);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Backspace' || e.key === 'Delete') {
                            const activeTarget = hoveredImage || selectedImage;
                            if (activeTarget) {
                                e.preventDefault();
                                handleDeleteImage(activeTarget.element);
                            }
                        }
                    }}
                    onClick={(e) => {
                        const img = e.target.tagName === 'IMG' ? e.target : null;
                        const anchor = !img ? (e.target.tagName === 'A' ? e.target : e.target.closest('.ql-editor a')) : null;
                        const targetEl = img || anchor;

                        if (targetEl && editorWrapperRef.current?.contains(targetEl)) {
                            const isLink = targetEl.tagName === 'A';
                            if (isLink) {
                                e.preventDefault();
                                if (e.ctrlKey || e.metaKey) {
                                    handleOpenEmbeddedElement({
                                        element: targetEl,
                                        src: targetEl.getAttribute('href'),
                                        isLink: true,
                                    });
                                    return;
                                }
                            }
                            e.stopPropagation();
                            const rect = targetEl.getBoundingClientRect();
                            const wrapperRect = editorWrapperRef.current?.getBoundingClientRect() || { top: 0, left: 0, width: 400 };
                            const relTop = rect.top - wrapperRect.top;
                            const relLeft = rect.left - wrapperRect.left;
                            const top = isLink
                                ? (relTop > 38 ? relTop - 34 : (rect.bottom - wrapperRect.top + 4))
                                : Math.max(6, relTop + 6);
                            const left = isLink
                                ? Math.max(8, Math.min(relLeft, wrapperRect.width - 155))
                                : Math.max(8, relLeft + rect.width - 155);

                            setSelectedImage({
                                element: targetEl,
                                src: targetEl.getAttribute(isLink ? 'href' : 'src'),
                                isLink,
                                top,
                                left,
                            });
                            if (!isLink) {
                                const editor = quillRef.current?.getEditor();
                                if (editor) {
                                    const blot = ReactQuill.Quill?.find?.(targetEl) || editor.scroll?.find?.(targetEl);
                                    if (blot) {
                                        const idx = editor.getIndex(blot);
                                        editor.setSelection(idx, 1);
                                        if (lastSelectionRangeRef.current) {
                                            lastSelectionRangeRef.current = { index: idx, length: 1 };
                                        }
                                    }
                                }
                            }
                        } else if (!e.target.closest('.image-floating-toolbar')) {
                            setSelectedImage(null);
                        }
                    }}
                    onErrorCapture={(e) => {
                        if (e.target.tagName === 'IMG') {
                            const img = e.target;
                            const src = img.getAttribute('src') || '';
                            if (src.startsWith('blob:') && getOptimisticImageUrl(src)) return;

                            const imageAtts = (taskAttachments || []).filter((a) => isImageFile(a));
                            const matched = imageAtts.find((a) => a._id && src.includes(String(a._id)))
                                || imageAtts.find((a) => a.originalName && a.originalName.length > 3 && src.includes(encodeURIComponent(a.originalName)));

                            if (matched && matched._id && taskId && taskId !== 'new') {
                                const previewUrl = getAttachmentPreviewUrl(taskId, matched._id);
                                if (img.src !== previewUrl) {
                                    img.src = previewUrl;
                                    setOptimisticImageUrl(matched._id, previewUrl);
                                    if (matched.originalName) setOptimisticImageUrl(matched.originalName, previewUrl);
                                    if (quillRef.current) {
                                        const editor = quillRef.current.getEditor();
                                        const currentHtml = editor.root.innerHTML;
                                        if (src && currentHtml.includes(src)) {
                                            const healed = currentHtml.replaceAll(src, previewUrl);
                                            editor.root.innerHTML = healed;
                                            setEditDescription(healed);
                                            scheduleAutoSave?.({ description: healed });
                                        }
                                    }
                                }
                            }
                        }
                    }}
                    className={`task-quill-wrapper rounded-xl transition-all border ${
                        isFullscreen ? 'is-fullscreen flex-1 flex flex-col min-h-0 overflow-hidden' : ''
                    } ${
                        mode === 'create' && itemType !== 'milestone' && isQuillEmpty(editDescription)
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-transparent'
                    }`}
                >
                    {/* Floating Actions Toolbar for Hovered or Selected File / Image */}
                    {(hoveredImage || selectedImage) && (
                        <div
                            className="image-floating-toolbar absolute z-30 flex items-center gap-1.5 p-1 bg-white/95 dark:bg-slate-800/95 shadow-xl border border-slate-200 dark:border-slate-700 rounded-lg backdrop-blur-xs animate-in fade-in duration-100 select-none"
                            style={{
                                top: `${(hoveredImage || selectedImage).top}px`,
                                left: `${(hoveredImage || selectedImage).left}px`,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={() => {
                                if (hoverLeaveTimerRef.current) {
                                    clearTimeout(hoverLeaveTimerRef.current);
                                    hoverLeaveTimerRef.current = null;
                                }
                            }}
                            onMouseLeave={() => setHoveredImage(null)}
                        >
                            <Tooltip title={(hoveredImage || selectedImage)?.isLink ? 'Open file in new tab' : 'View full size in new tab'}>
                                <button
                                    type="button"
                                    onClick={() => handleOpenEmbeddedElement(hoveredImage || selectedImage)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer border-none bg-transparent transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[15px] text-blue-600 dark:text-blue-400">
                                        {(hoveredImage || selectedImage)?.isLink ? 'open_in_new' : 'zoom_in'}
                                    </span>
                                    <span>{(hoveredImage || selectedImage)?.isLink ? 'Open' : 'View'}</span>
                                </button>
                            </Tooltip>
                            <div className="w-[1px] h-3.5 bg-slate-200 dark:bg-slate-700" />
                            <Tooltip title={(hoveredImage || selectedImage)?.isLink ? 'Delete file from description' : 'Delete image from description'}>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteImage((hoveredImage || selectedImage)?.element)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded cursor-pointer border-none bg-transparent transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[15px] text-red-500">delete</span>
                                    <span>Delete</span>
                                </button>
                            </Tooltip>
                        </div>
                    )}

                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={editDescription || ''}
                        onChange={(val) => {
                            setEditDescription(val);
                            if (mode === 'view' && taskId && taskId !== 'new') {
                                scheduleAutoSave?.({ description: val });
                                if (isQuillEmpty(val) && (knownEmbeddedAttachmentIdsRef.current?.length || 0) > 0) {
                                    cleanupOrphanDescriptionAttachments(val);
                                }
                            }
                        }}
                        onChangeSelection={(range) => {
                            if (range) {
                                lastSelectionRangeRef.current = range;
                            }
                        }}
                        onBlur={handleEditorBlur}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Write a detailed description… You can format text, add bullet points, links, and drop images."
                    />

                    {/* Referenced file chips */}
                    {mentionedFileNames.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50/60 dark:bg-[#1a2234]/60 border-t border-slate-100 dark:border-slate-800 rounded-b-xl">
                            <span className="text-[11px] font-semibold text-slate-400 self-center mr-1">
                                Referenced files:
                            </span>
                            {mentionedFileNames.map((fn, idx) => (
                                <DescriptionFileMentionChip
                                    key={`desc-file-${idx}`}
                                    fileName={fn}
                                    taskId={taskId}
                                    taskAttachments={taskAttachments}
                                    onRemoveFile={handleRemoveFileReference}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
